import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import Sections from "../utils/sections";
import HtmlPreviewSection from "../utils/HtmlPreviewSection";
import successToast from "../../../helper-components/success-toast";
import errorToast from "../../../helper-components/error-toast";
import { formatString } from "../../../helper-components/helper-function";
import {
  printPreferenceListAction,
  printPreferenceUpdateAction,
  BIllingsTypesAction,
} from "../../../../redux/actions/settings-action";
import preferencesData from "../data/preferences.json";

const PrintPreferenceTabs = (ParentData) => {
  const { type } = ParentData;
  const dispatch = useDispatch();

  const [preferences] = useState(preferencesData);
  const [editedData, setEditedData] = useState({});
  const [serverData, setServerData] = useState({});

  // html previews
  const [htmlData, setHtmlData] = useState([]);

  // loading & error states
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [previewsLoading, setPreviewsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [previewsError, setPreviewsError] = useState(null);

  const refreshPreview = () => {
    setPreviewsLoading(true);
    setPreviewsError(null);
    dispatch(
      BIllingsTypesAction(
        { type },
        (res) => {
          setHtmlData(res?.data || []);
          setPreviewsLoading(false);
        },
        (err) => {
          setHtmlData([]);
          setPreviewsError(formatString(err?.message) || "Failed to load previews");
          setPreviewsLoading(false);
          errorToast(formatString(err?.message));
        }
      )
    );
  };

  useEffect(() => {
    setPrefsLoading(true);
    setError(null);

    // Load preferences
    dispatch(
      printPreferenceListAction(
        { type },
        (res) => {
          const payload = res?.data || {};
          setServerData(payload);
          setEditedData(payload);
          setPrefsLoading(false);
        },
        (err) => {
          setError(formatString(err?.message) || "Failed to fetch preferences");
          setPrefsLoading(false);
        }
      )
    );

    // Load HTML previews
    refreshPreview();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const onSave = () => {
    setSaving(true);
    dispatch(
      printPreferenceUpdateAction(
        { type, ...editedData },
        () => {
          successToast("Updated Print Preferences");
          setServerData(editedData);
          setSaving(false);
        },
        (err) => {
          errorToast(formatString(err?.message));
          setSaving(false);
        }
      )
    );
  };

  const isDirty =
    JSON.stringify(serverData ?? {}) !== JSON.stringify(editedData ?? {});

  // Page-level loading (preferences)
  if (prefsLoading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="inline-flex items-center gap-3 text-secondary">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-D0D5DD border-t-transparent" />
          <span className="text-sm">Loading preferences…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-8 mt-6 ml-0">
      {error && (
        <div className="mb-4 rounded-md bg-F94B4B/10 text-F51D00 border border-F94B4B/30 px-3 py-2 text-sm">
          {error}
        </div>
      )}

      {/* SCROLLABLE CONTENT AREA */}
      <div className="grid gap-6 max-h-[70vh] overflow-y-auto pr-2 scrollbar-thin">
        {/* Preferences form sections */}
        <Sections
          data={preferences}
          editData={editedData}
          setEditedData={setEditedData}
        />

        {/* HTML Preview area */}
        <div className="rounded-lg border border-EAECF0 bg-white">
          <div className="flex items-center justify-between px-4 py-3 border-b border-EAECF0">
            <h3 className="text-sml font-semibold text-000000">
              HTML Preview Formats
            </h3>
            <button
              type="button"
              onClick={refreshPreview}
              className="px-3 py-1.5 rounded-md border border-D0D5DD text-secondary hover:bg-f3f8ff text-xs"
            >
              Refresh
            </button>
          </div>

          <div className="p-4">
            {previewsLoading ? (
              <div className="flex items-center gap-3 text-secondary">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-D0D5DD border-t-transparent" />
                <span className="text-sm">Loading previews…</span>
              </div>
            ) : previewsError ? (
              <div className="rounded-md bg-F94B4B/10 text-F51D00 border border-F94B4B/30 px-3 py-2 text-sm">
                {previewsError}
              </div>
            ) : htmlData?.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-8">
                <div className="h-10 w-10 rounded-full bg-f3f8ff grid place-items-center">
                  <span className="text-277DFE text-lg">ℹ</span>
                </div>
                <div className="text-sm text-667085">
                  No formats available for{" "}
                  <span className="font-medium text-000000">{type}</span>.
                </div>
                <div className="text-xs text-667085">
                  Create and save a new format to see it here.
                </div>
              </div>
            ) : (
              <HtmlPreviewSection
                data={htmlData}
                editData={editedData}
                setEditedData={setEditedData}
              />
            )}
          </div>
        </div>
      </div>

      {/* NORMAL (NON-STICKY) FOOTER WITH SAVE BUTTON */}
      <div className="mt-6 border-t border-EAECF0 bg-white px-0 py-3">
        <div className="flex justify-end">
          <button
            type="button"
            disabled={!isDirty || saving}
            onClick={onSave}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-white transition ${
              !isDirty || saving
                ? "bg-disabled-color cursor-not-allowed"
                : "bg-primary hover:opacity-90"
            }`}
          >
            {saving && (
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/60 border-t-transparent" />
            )}
            <span>{saving ? "Saving…" : "Save"}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintPreferenceTabs;
